<template>
  <div>
  <div v-if="isLoading">
    <CanPayLoader/>
  </div>
  <div class="container" style="height: 100vh;">
  <div class="gift-card-share">
    <div class="share-form">
      <div class="form-header">
        <div class="header-content">
          <div class="back-button" @click="goBack">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="26" viewBox="0 0 68 56" fill="none">
<path d="M2.75613 25.5833L26.9779 1C28.2917 -0.333333 30.4265 -0.333333 31.7402 1C33.0539 2.33333 33.0539 4.5 31.7402 5.83333L13.2659 24.5833H64.0086C65.8971 24.5833 67.375 26.0833 67.375 28C67.375 29.9167 65.8971 31.4167 64.0086 31.4167H13.2659L31.7402 50.1667C33.0539 51.5 33.0539 53.6667 31.7402 55C31.0833 55.6667 30.2623 56 29.3591 56C28.4559 56 27.6348 55.6667 26.9779 55L2.75613 30.4167L0.375 28L2.75613 25.5833Z" fill="white"/>
</svg>
          </div>
          <h1 class="form-title">Send Gift Card</h1>
        </div>
      </div>

      <div class="desktop-content">
        <div class="left-column">
          <!-- Show error message if no share data -->
          <div class="gift-card-type-section" v-if="!shareData">
            <div class="gift-card-type-title">Error</div>
            <div class="error-message">
              <p>No gift card data found. Please go back to My Gift Cards and try again.</p>
            </div>
          </div>

          <div class="gift-card-type-section" v-if="shareData">
            <div class="gift-card-type-title">Gift Card Details</div>
            <div class="gift-card-info">
              <div class="info-row">
                <span class="info-label">Card Number:</span>
                <span class="info-value">{{ shareData.gift_card_number || 'N/A' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Amount:</span>
                <span class="info-value">${{ parseFloat(shareData.amount || 0).toFixed(2) }}</span>
              </div>
              <div class="info-row" v-if="shareData.merchant_retailer">
                <span class="info-label">Merchant/Retailer:</span>
                <span class="info-value">{{ shareData.merchant_retailer }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="info-value">{{ shareData.status === 'Success' ? 'Active' : shareData.status }}</span>
              </div>
            </div>
          </div>

          <div class="gift-card-type-section">
            <div class="gift-card-type-title">Send Gift Card to:</div>
            <div class="form-inputs">
              <input type="text" v-model="recipientName" placeholder="Name" :class="['form-input', { 'form-input-error': validationErrors.name }]" @input="clearValidationError('name')" required />
              <input type="tel" v-model="recipientMobile" placeholder="Mobile #" :class="['form-input', { 'form-input-error': validationErrors.phone }]" v-mask="'(###) ###-####'" @input="clearValidationError('phone')" required />
              <input type="email" v-model="recipientEmail" placeholder="Email" :class="['form-input', { 'form-input-error': validationErrors.email }]" @input="clearValidationError('email')" required />
            </div>
          </div>

          <div class="gift-card-type-section">
            <div class="gift-card-type-title">Message</div>
            <div class="form-inputs">
              <textarea v-model="giftMessage" placeholder="Enjoy the gift card!" class="form-textarea" rows="4"></textarea>
            </div>
          </div>
        </div>

        <div class="continue-section">
          <button class="continue-btn" @click="shareGiftCard()" :disabled="!shareData">
            Send Gift Card
          </button>
        </div>
      </div>
    </div>
    <gift-card-error ref="GiftCardDetailsErrorModal" :textMessage="errorMessage"></gift-card-error>
    <gift-card-success ref="GiftCardSuccessModal" :textMessage="successMessage" @buy-more="goToBuyMore" @go-home="goToHome"></gift-card-success>
  </div>
  </div>
  </div>
</template>

<script>
import GiftCardError from "./Modal/GiftCardError.vue";
import GiftCardSuccess from "./Modal/GiftCardSuccess.vue";
import api from "../../api/giftcard.js";
import CanPayLoader from "../CustomLoader/CanPayLoader.vue";
export default {
  name: 'CardDetails',
  components: {
    GiftCardError,
    GiftCardSuccess,
    CanPayLoader
  },
  data() {
    return {
      recipientName: '',
      recipientMobile: '',
      recipientEmail: '',
      giftMessage: 'Enjoy the gift card!',
      isLoading: false,
      errorMessage: '',
      successMessage: 'Gift card shared successfully!',
      shareData: null,
      validationErrors: {
        name: false,
        phone: false,
        email: false
      }
    }
  },
  mounted() {
    this.$root.$emit("changeWhiteBackground", [false, false, "PayDashboardHeader"]);

    // Load gift card share data
    const shareData = localStorage.getItem('gift_card_share_data');
    if (shareData) {
      try {
        this.shareData = JSON.parse(shareData);
        // If no share data is found, redirect back to my gift cards
        if (!this.shareData || this.shareData.mode !== 'share') {
          this.$router.push('/my-gift-cards');
          return;
        }
      } catch (e) {
        console.error('Error parsing share data:', e);
        localStorage.removeItem('gift_card_share_data');
        this.$router.push('/my-gift-cards');
      }
    } else {
      // No share data found, redirect to my gift cards
      this.$router.push('/my-gift-cards');
    }
  },
  beforeDestroy() {
    // Clean up share data if user navigates away without completing the share
    // The successful share will already have removed this data
  },
  methods: {
    goBack() {
      // Clear share data and go back to my gift cards
      localStorage.removeItem('gift_card_share_data');
      this.$router.push('/my-gift-cards');
    },
    validateRecipientDetails() {
      // Reset validation errors
      this.validationErrors = {
        name: false,
        phone: false,
        email: false
      };

      const name = (this.recipientName || '').trim();
      const phone = this.recipientMobile.replace(/[()\s-]/g, '');
      const email = (this.recipientEmail || '').trim();
      const consumer_storage_data = localStorage.getItem('consumer_login_response');
      const consumer_data = JSON.parse(consumer_storage_data);

      let isValid = true;

      // Check if sharing with self
      if(phone == consumer_data.phone){
        if(phone == consumer_data.phone) this.validationErrors.phone = true;
        isValid = false;
      }

      // Validate name
      if (!name) {
        this.validationErrors.name = true;
        isValid = false;
      }

      // Validate phone
      if (!phone) {
        this.validationErrors.phone = true;
        isValid = false;
      } else if(phone.length !== 10){
        this.validationErrors.phone = true;
        isValid = false;
      } else {
        // Basic phone validation (adjust regex as needed)
        const phoneRegex = /^[\d\s\-\+\(\)]+$/;
        if (!phoneRegex.test(phone)) {
          this.validationErrors.phone = true;
          isValid = false;
        }
      }

      // Validate email
      if (!email) {
        this.validationErrors.email = true;
        isValid = false;
      } else {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          this.validationErrors.email = true;
          isValid = false;
        }
      }

      return isValid;
    },
    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.validationErrors[field] = false;
      }
    },
    shareGiftCard() {
      if (!this.validateRecipientDetails()) {
        // Validation errors are now shown via red outline styling
        return;
      }

      if (!this.shareData || !this.shareData.gift_card_id) {
        this.errorMessage = 'Gift card data not found. Please try again.';
        this.$refs['GiftCardDetailsErrorModal'] && this.$refs['GiftCardDetailsErrorModal'].showErrorModal();
        return;
      }

      const payload = {
        gift_card_id: this.shareData.gift_card_id,
        to_name: this.recipientName.trim(),
        to_phone: this.recipientMobile.trim().replace(/[()\s-]/g, ''),
        to_email: this.recipientEmail.trim(),
        message: this.giftMessage.trim() || ''
      };

      this.isLoading = true;
      api.shareGiftCard(payload)
        .then((response) => {
          this.isLoading = false;
          if (response.code === 200) {
            // Clear share data from localStorage
            localStorage.removeItem('gift_card_share_data');
            this.successMessage = 'Gift card shared successfully!';
            this.$refs['GiftCardSuccessModal'] && this.$refs['GiftCardSuccessModal'].showSuccessModal();
            setTimeout(()=>{
                this.$router.push('/my-gift-cards');
            },3000)
          } else {
            this.errorMessage = response.message || 'Unable to send gift card';
            this.$refs['GiftCardDetailsErrorModal'] && this.$refs['GiftCardDetailsErrorModal'].showErrorModal();
          }
        })
        .catch((err) => {
          this.isLoading = false;
          const msg = (err && err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Something went wrong. Please try again.';
          this.errorMessage = msg;
          this.$refs['GiftCardDetailsErrorModal'] && this.$refs['GiftCardDetailsErrorModal'].showErrorModal();
        });
    },


    goToBuyMore() {
      this.$router.push('/my-gift-cards');
    },
    goToHome() {
      this.$router.push('/my-gift-cards');
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/components/giftcard.scss';

/* Custom styles for CardDetails alignment */
.header-content {
  margin: 0 5px; /* Match the margin of gift-card-type-section */
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
}

.back-button {
  cursor: pointer;
  padding: 8px 8px 8px 0;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 32px;
  min-height: 26px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateX(-2px);
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.2);
    transform: translateX(-1px);
  }
}

/* Desktop alignment */
@media (min-width: 768px) {
  .header-content {
    margin: 0; /* Remove margin on desktop as gift-card-type-section has no margin */
  }
}

/* Large desktop alignment */
@media (min-width: 1200px) {
  .header-content {
    margin: 0; /* Keep no margin on large desktop */
  }
}

/* Form inputs styling */
.form-inputs {
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-input {
  background: transparent !important;
  border: 1px solid white;
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 16px;
  font-family: 'Open Sans', sans-serif;

  &::placeholder {
    color: #ffffff!important;
    font-size: 16px;
    font-weight: 500;
  }

  &:focus {
    outline: none;
    border-color: white;
    background: transparent !important;
  }

  &:hover {
    border-color: white;
    background: transparent !important;
  }

  &.form-input-error {
    border-color: #ffbcbc !important;

    &:focus {
      border-color: #ffbcbc !important;
    }

    &:hover {
      border-color: #ffbcbc !important;
    }
  }
}

.form-textarea {
  background: transparent !important;
  border: 1px solid white;
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 16px;
  font-family: 'Open Sans', sans-serif;
  resize: vertical;
  min-height: 80px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.8);
    font-size: 16px;
  }

  &:focus {
    outline: none;
    border-color: white;
    background: transparent !important;
  }

  &:hover {
    border-color: white;
    background: transparent !important;
  }
}

/* Gift card info styling */
.gift-card-info {
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.info-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 400;
}

.info-value {
  color: white;
  font-size: 14px;
  font-weight: 600;
}

/* Disabled button styling */
.continue-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: rgba(255, 255, 255, 0.3) !important;
}

.continue-btn:disabled:hover {
  background-color: rgba(255, 255, 255, 0.3) !important;
  transform: none;
}

/* Error message styling */
.error-message {
  padding: 15px;
  border: 1px solid rgba(255, 100, 100, 0.5);
  border-radius: 8px;
  background: rgba(255, 100, 100, 0.1);
}

.error-message p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin: 0;
  text-align: center;
}
</style>
